import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

interface ArchitectureStatus {
  mode: 'legacy' | 'v2';
  switched_at: string | null;
  switched_by: string | null;
  railway: {
    status: 'online' | 'offline' | 'unknown';
    latency: number;
  };
  activity: {
    last_conversation: string | null;
    today_count: number;
  };
}

export default function ArchitectureSwitch() {
  const [status, setStatus] = useState<ArchitectureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [showConfirm, setShowConfirm] = useState<'legacy' | 'v2' | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await apiFetch('/api/settings/architecture');
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Architecture status fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = async (targetMode: 'legacy' | 'v2') => {
    setSwitching(true);
    try {
      const res = await apiFetch('/api/settings/architecture/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: targetMode })
      });
      if (res.ok) {
        await fetchStatus();
      }
    } catch (err) {
      console.error('Switch failed:', err);
    } finally {
      setSwitching(false);
      setShowConfirm(null);
    }
  };

  const isV2 = status?.mode === 'v2';
  const railwayOnline = status?.railway.status === 'online';

  if (loading) {
    return (
      <div className="card" style={{ background: 'var(--bg-surface)' }}>
        <div className="flex items-center justify-center py-8">
          <div className="text-text-muted animate-pulse">
            載入架構狀態...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: isV2
            ? 'linear-gradient(135deg, rgba(233, 30, 140, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)'
            : 'linear-gradient(135deg, rgba(100, 100, 120, 0.08) 0%, rgba(60, 60, 80, 0.08) 100%)',
          border: `1px solid ${isV2 ? 'rgba(233, 30, 140, 0.2)' : 'var(--border)'}`,
        }}
      >
        {/* Header */}
        <div className="p-5 pb-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="text-2xl"
                style={{ filter: isV2 ? 'none' : 'grayscale(0.5)' }}
              >
                {isV2 ? '🚀' : '⚙️'}
              </div>
              <div>
                <h2 className="text-lg font-semibold">靈魂搬家</h2>
                <p className="text-xs text-text-muted">Soul Transfer</p>
              </div>
            </div>
            {/* 狀態指示燈 */}
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{
                  background: railwayOnline ? 'var(--green)' : 'var(--red)',
                  boxShadow: railwayOnline
                    ? '0 0 8px rgba(0, 230, 118, 0.5)'
                    : '0 0 8px rgba(255, 82, 82, 0.5)',
                }}
              />
              <span className="text-xs text-text-muted">
                {railwayOnline ? 'Railway 運作中' : 'Railway 離線'}
              </span>
            </div>
          </div>
        </div>

        {/* 雙欄切換區 */}
        <div className="p-5">
          <div className="grid grid-cols-2 gap-3">
            {/* 舊架構 */}
            <button
              onClick={() => {
                if (isV2) setShowConfirm('legacy');
              }}
              disabled={!isV2 || switching}
              className="relative rounded-xl p-4 text-left transition-all duration-300"
              style={{
                background: !isV2
                  ? 'rgba(100, 100, 120, 0.15)'
                  : 'rgba(40, 40, 50, 0.3)',
                border: !isV2
                  ? '2px solid rgba(100, 100, 120, 0.4)'
                  : '2px solid transparent',
                cursor: isV2 ? 'pointer' : 'default',
                opacity: switching ? 0.5 : 1,
              }}
            >
              {/* Active 指示器 */}
              {!isV2 && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute top-3 right-3"
                  initial={false}
                >
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ background: 'rgba(100, 100, 120, 0.3)', color: 'var(--text-secondary)' }}
                  >
                    使用中
                  </span>
                </motion.div>
              )}

              <div className="text-xl mb-2">⚙️</div>
              <div className="font-semibold text-sm">舊架構</div>
              <div className="text-xs text-text-muted mt-1">Worker v3.9.1</div>
              <div className="mt-3 space-y-1">
                <div className="text-xs text-text-muted flex items-center gap-1.5">
                  <span style={{ color: 'var(--red)' }}>●</span> 記憶會交叉污染
                </div>
                <div className="text-xs text-text-muted flex items-center gap-1.5">
                  <span style={{ color: 'var(--red)' }}>●</span> 沒有日記系統
                </div>
                <div className="text-xs text-text-muted flex items-center gap-1.5">
                  <span style={{ color: 'var(--green)' }}>●</span> 穩定運作中
                </div>
              </div>
            </button>

            {/* 新架構 */}
            <button
              onClick={() => {
                if (!isV2) setShowConfirm('v2');
              }}
              disabled={isV2 || switching || !railwayOnline}
              className="relative rounded-xl p-4 text-left transition-all duration-300"
              style={{
                background: isV2
                  ? 'rgba(233, 30, 140, 0.1)'
                  : 'rgba(40, 40, 50, 0.3)',
                border: isV2
                  ? '2px solid rgba(233, 30, 140, 0.4)'
                  : '2px solid transparent',
                cursor: !isV2 && railwayOnline ? 'pointer' : isV2 ? 'default' : 'not-allowed',
                opacity: switching ? 0.5 : (!isV2 && !railwayOnline) ? 0.4 : 1,
              }}
            >
              {/* Active 指示器 */}
              {isV2 && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute top-3 right-3"
                  initial={false}
                >
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ background: 'rgba(233, 30, 140, 0.2)', color: 'var(--pink)' }}
                  >
                    使用中
                  </span>
                </motion.div>
              )}

              <div className="text-xl mb-2">🚀</div>
              <div className="font-semibold text-sm" style={{ color: isV2 ? 'var(--pink)' : 'var(--text-primary)' }}>
                新架構 v2.0
              </div>
              <div className="text-xs text-text-muted mt-1">Railway + PostgreSQL</div>
              <div className="mt-3 space-y-1">
                <div className="text-xs text-text-muted flex items-center gap-1.5">
                  <span style={{ color: 'var(--green)' }}>●</span> 記憶完全隔離
                </div>
                <div className="text-xs text-text-muted flex items-center gap-1.5">
                  <span style={{ color: 'var(--green)' }}>●</span> 日記 + 夢境
                </div>
                <div className="text-xs text-text-muted flex items-center gap-1.5">
                  <span style={{ color: 'var(--green)' }}>●</span> 三層防護回滾
                </div>
              </div>
            </button>
          </div>

          {/* 活動資訊 */}
          {status?.activity && (
            <div
              className="mt-4 flex items-center justify-between px-3 py-2 rounded-lg text-xs"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <div className="text-text-muted">
                今日對話：
                <span className="text-text-primary font-medium ml-1">{status.activity.today_count} 則</span>
              </div>
              {status.activity.last_conversation && (
                <div className="text-text-muted">
                  最近：{formatTimeAgo(status.activity.last_conversation)}
                </div>
              )}
            </div>
          )}

          {/* 切換時間 */}
          {status?.switched_at && (
            <div className="mt-2 text-center text-xs text-text-muted">
              上次切換：{new Date(status.switched_at).toLocaleString('zh-TW')}
            </div>
          )}
        </div>

        {/* 提醒 */}
        <div
          className="px-5 py-3 text-xs text-text-muted flex items-center gap-2"
          style={{ borderTop: '1px solid var(--border-light)', background: 'rgba(0,0,0,0.1)' }}
        >
          <span>💡</span>
          <span>
            {isV2
              ? '新架構運作中。如需回滾，切回舊架構後需到 LINE Developers 改 Webhook URL'
              : '切換新架構前，請先確認 Railway 後端 + 資料庫 已就緒'
            }
          </span>
        </div>
      </motion.div>

      {/* 確認對話框 */}
      <AnimatePresence>
        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-sm rounded-2xl p-6"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <div className="text-center mb-6">
                <div className="text-4xl mb-3">
                  {showConfirm === 'v2' ? '🚀' : '⚠️'}
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {showConfirm === 'v2' ? '切換到新架構？' : '切回舊架構？'}
                </h3>
                <p className="text-sm text-text-secondary">
                  {showConfirm === 'v2'
                    ? '包容將透過 Railway 後端回應，記憶隔離、日記系統啟動'
                    : '包容將回到舊 Worker，記憶可能交叉污染'
                  }
                </p>
              </div>

              {showConfirm === 'v2' && (
                <div
                  className="mb-4 p-3 rounded-lg text-xs space-y-1"
                  style={{ background: 'rgba(233, 30, 140, 0.05)', border: '1px solid rgba(233, 30, 140, 0.15)' }}
                >
                  <div className="font-medium" style={{ color: 'var(--pink)' }}>切換後請記得：</div>
                  <div className="text-text-muted">1. 到 LINE Developers 改 Webhook URL</div>
                  <div className="text-text-muted">2. 自己先傳一句測試訊息</div>
                  <div className="text-text-muted">3. 確認正常才通知 Daddy</div>
                </div>
              )}

              {showConfirm === 'legacy' && (
                <div
                  className="mb-4 p-3 rounded-lg text-xs space-y-1"
                  style={{ background: 'rgba(255, 82, 82, 0.05)', border: '1px solid rgba(255, 82, 82, 0.15)' }}
                >
                  <div className="font-medium" style={{ color: 'var(--red)' }}>回滾步驟：</div>
                  <div className="text-text-muted">1. LINE Developers → Webhook URL 改回舊的</div>
                  <div className="text-text-muted">2. 舊 Worker 從未刪除，立即生效</div>
                  <div className="text-text-muted">3. 30 秒內完成</div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(null)}
                  className="btn-secondary flex-1"
                  disabled={switching}
                >
                  取消
                </button>
                <button
                  onClick={() => handleSwitch(showConfirm)}
                  disabled={switching}
                  className="flex-1 py-2.5 rounded-xl font-medium text-sm text-white transition-all"
                  style={{
                    background: showConfirm === 'v2'
                      ? 'linear-gradient(135deg, var(--pink) 0%, var(--purple) 100%)'
                      : 'var(--red)',
                    opacity: switching ? 0.5 : 1,
                  }}
                >
                  {switching ? '切換中...' : showConfirm === 'v2' ? '確認搬家' : '確認回滾'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diff < 60) return '剛剛';
  if (diff < 3600) return `${Math.floor(diff / 60)} 分鐘前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小時前`;
  return `${Math.floor(diff / 86400)} 天前`;
}
